import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { RatingPrompt } from '@/components/order/RatingPrompt';
import toast from 'react-hot-toast';
import { TrackingModal } from '@/components/order/TrackingModal';
import { ComplaintModal } from '@/components/order/ComplaintModal';

interface Dispute {
  reason: string;
  notes?: string;
  responded: boolean;
  buyerDisputeCount: number;
}
interface Sale {
  id: string;
  reference: string;
  productTitle: string;
  productImage?: string;
  amountGBP: number;
  payoutGBP: number;
  state: 'AWAITING_DISPATCH' | 'AT_LOCKER' | 'COLLECTED' | 'PAID_OUT' | 'DISPUTED';
  buyerHandle: string;
  method?: 'locker' | 'standard' | 'express';
  trackingCode?: string;
  lockerDropQrUrl?: string;
  dropoffDone?: boolean;
  dispute?: Dispute;
}

const REASON_LABELS: Record<string, string> = {
  WRONG_ITEM: 'Wrong item sent', EMPTY_PARCEL: 'Empty / item missing', NOT_AS_DESCRIBED: 'Not as described',
  NOT_RECEIVED: 'Never arrived', DAMAGED: 'Arrived damaged', COUNTERFEIT: 'Suspected counterfeit',
};

export default function Sales() {
  const qc = useQueryClient();
  const { data: sales = [] } = useQuery<Sale[]>({
    queryKey: ['sales'],
    queryFn: async () => (await api.get('/me/sales')).data,
  });

  // Proof-of-dispatch drop-off
  const [dropSale, setDropSale] = useState<Sale | null>(null);
  const [dropPhoto, setDropPhoto] = useState<File | null>(null);
  const drop = useMutation({
    mutationFn: async ({ id, fd }: { id: string; fd: FormData }) =>
      (await api.post(`/orders/${id}/confirm-dropoff`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data,
    onSuccess: () => { toast.success('Drop-off confirmed with proof. The buyer can now collect.'); setDropSale(null); setDropPhoto(null); qc.invalidateQueries({ queryKey: ['sales'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not confirm drop-off.'),
  });
  function submitDrop() {
    if (!dropSale || !dropPhoto) { toast.error('Add a photo of the packed item and label.'); return; }
    const fd = new FormData();
    fd.append('dropoffPhoto', dropPhoto);
    drop.mutate({ id: dropSale.id, fd });
  }

  const [trackSale, setTrackSale] = useState<Sale | null>(null);
  const [complaintSale, setComplaintSale] = useState<Sale | null>(null);
  const [labelSale, setLabelSale] = useState<Sale | null>(null);
  const [labelLoading, setLabelLoading] = useState(false);

  // Centralised model: Souqli generated the label with its own carrier account.
  // The courier PDF sits behind auth, so we fetch it through the authenticated
  // API client and open the blob - a plain link wouldn't carry the token.
  async function openLabelPdf(orderId: string, reference?: string) {
    setLabelLoading(true);
    try {
      const res = await api.get(`/orders/${orderId}/label`, { responseType: 'blob' });
      const blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Download the label to the seller's machine (works for InPost, Royal Mail
      // and DPD - the server returns the same courier PDF from Sendcloud).
      const a = document.createElement('a');
      a.href = url;
      a.download = `souqli-label-${reference ?? orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success('Label downloaded.');
    } catch (e: any) {
      // The error body comes back as a blob on a blob request - read the real reason.
      let msg = 'Could not fetch the label just now. Please try again in a moment.';
      try {
        const b = e?.response?.data;
        if (b instanceof Blob) { const j = JSON.parse(await b.text()); if (j?.message) msg = j.message; }
        else if (b?.message) msg = b.message;
      } catch { /* keep default */ }
      toast.error(msg);
    } finally {
      setLabelLoading(false);
    }
  }

  // Dispute defense
  const [respSale, setRespSale] = useState<Sale | null>(null);
  const [respText, setRespText] = useState('');
  const [respPhoto, setRespPhoto] = useState<File | null>(null);
  const respond = useMutation({
    mutationFn: async ({ id, fd }: { id: string; fd: FormData }) =>
      (await api.post(`/orders/${id}/dispute-response`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data,
    onSuccess: () => { toast.success('Response sent. Souqli will review both sides.'); setRespSale(null); setRespText(''); setRespPhoto(null); qc.invalidateQueries({ queryKey: ['sales'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not send response.'),
  });
  function submitResponse() {
    if (!respSale || respText.trim().length < 2) { toast.error('Add a short explanation.'); return; }
    const fd = new FormData();
    fd.append('response', respText.trim());
    if (respPhoto) fd.append('photo', respPhoto);
    respond.mutate({ id: respSale.id, fd });
  }

  const totalPending = sales.filter((s) => s.state !== 'PAID_OUT').reduce((a, s) => a + s.payoutGBP, 0);

  return (
    <div>
      <div className="stamp text-gold-dark">ORDERS TO FULFIL</div>
      <h1 className="display text-4xl mt-2">Sales</h1>
      <div className="mt-6"><RatingPrompt /></div>
      <p className="text-sm text-mute mt-2 max-w-[60ch]">
        Drop your parcel at any partner locker and upload a proof-of-dispatch photo. Funds release to you on buyer collection, or automatically after the collection window.
      </p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Active sales"    value={String(sales.filter((s) => s.state !== 'PAID_OUT').length)} />
        <Stat label="Awaiting drop"   value={String(sales.filter((s) => s.state === 'AWAITING_DISPATCH').length)} accent="bg-gold/10" />
        <Stat label="In dispute"      value={String(sales.filter((s) => s.state === 'DISPUTED').length)} accent="bg-bad/10" />
        <Stat label="Pending payout"  value={`£${totalPending.toFixed(2)}`} accent="bg-clay/10" />
      </div>

      <div className="mt-8 space-y-4">
        {sales.map((s) => (
          <div key={s.id} className={`card p-5 ${s.state === 'DISPUTED' ? 'border-bad/40' : ''}`}>
            <div className="grid lg:grid-cols-12 gap-5 items-center">
              <div className="lg:col-span-5 flex items-center gap-4">
                <div className="w-20 h-20 notched-sm bg-gradient-to-br from-ink-700 to-ink shrink-0"
                  style={s.productImage ? { backgroundImage: `url(${s.productImage})`, backgroundSize: 'cover' } : undefined} />
                <div>
                  <div className="stamp text-mute mono">{s.reference}</div>
                  <div className="display text-xl">{s.productTitle}</div>
                  <div className="text-xs text-mute mt-0.5">Buyer: {s.buyerHandle}</div>
                </div>
              </div>
              <div className="lg:col-span-2">
                <span className={`pill ${s.state === 'DISPUTED' ? 'pill-bad' : 'pill-gold'}`}>{s.state.replace(/_/g, ' ').toLowerCase()}</span>
              </div>
              <div className="lg:col-span-2">
                <div className="display text-xl">£{s.amountGBP.toFixed(2)}</div>
                <div className="stamp text-mute">+£{s.payoutGBP.toFixed(2)} to you</div>
              </div>
              <div className="lg:col-span-3 flex flex-col gap-1.5">
                {s.state === 'AWAITING_DISPATCH' && (
                  <>
                    <button onClick={() => setLabelSale(s)} className="btn btn-sm btn-gold !text-ink">
                      🏷️ {s.method === 'locker' ? 'Show locker QR' : 'Print label'}
                    </button>
                    <button onClick={() => { setDropSale(s); setDropPhoto(null); }} className="btn btn-sm btn-outline">📦 Confirm drop-off</button>
                  </>
                )}
                {s.state === 'DISPUTED' && !s.dispute?.responded && (
                  <button onClick={() => { setRespSale(s); setRespText(''); setRespPhoto(null); }} className="btn btn-sm bg-bad text-cream hover:opacity-90">Respond to dispute</button>
                )}
                {s.state === 'DISPUTED' && s.dispute?.responded && (
                  <span className="pill pill-warn self-start text-[9px]">Response sent · under review</span>
                )}
                {s.state !== 'PAID_OUT' && (
                  <button onClick={() => setTrackSale(s)} className="btn btn-sm btn-ghost border border-warmline text-center">📍 Track</button>
                )}
                <button onClick={() => setComplaintSale(s)} className="text-[11px] text-mute hover:text-bad text-center">Report buyer</button>
              </div>
            </div>

            {/* Dispute detail banner (seller protection) */}
            {s.state === 'DISPUTED' && s.dispute && (
              <div className="mt-4 pt-4 border-t border-bad/20 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px]">
                <span className="text-bad font-medium">⚠ Buyer reported: {REASON_LABELS[s.dispute.reason] ?? s.dispute.reason}</span>
                {s.dispute.notes && <span className="text-mute">“{s.dispute.notes}”</span>}
                <span className={`stamp text-[9px] ${s.dispute.buyerDisputeCount >= 3 ? 'text-bad' : 'text-mute'}`}>
                  BUYER DISPUTE HISTORY: {s.dispute.buyerDisputeCount}{s.dispute.buyerDisputeCount >= 3 ? ' · HIGH - FLAGGED' : ''}
                </span>
                <span className="stamp text-mute text-[9px]">FUNDS HELD · YOUR PAYOUT PAUSED</span>
              </div>
            )}
          </div>
        ))}
        {!sales.length && (
          <div className="card p-10 text-center">
            <div className="display text-2xl text-mute">No active sales yet</div>
            <p className="text-sm text-mute mt-2">Your live listings will appear here once someone buys.</p>
          </div>
        )}
      </div>

      {/* Drop-off proof modal */}
      {dropSale && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setDropSale(null)}>
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="stamp text-gold-dark">Confirm drop-off</div>
            <h2 className="display text-2xl mt-1">{dropSale.productTitle}</h2>
            <p className="text-sm text-mute mt-2">
              Drop the parcel at the locker, then upload a photo of the packed item next to its shipping label. This is your proof of what you sent - it protects you if the buyer later disputes.
            </p>
            <label className="mt-5 flex items-center gap-3 p-3 border-2 border-dashed border-warmline rounded-luxe cursor-pointer hover:border-gold transition">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setDropPhoto(e.target.files?.[0] ?? null)} />
              <span className="text-xl">📦</span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">{dropPhoto ? dropPhoto.name : 'Photo of packed item + label'}</div>
                <div className="text-[11px] text-mute">{dropPhoto ? 'Tap to replace' : 'Required'}</div>
              </div>
              {dropPhoto && <span className="pill pill-ok text-[9px]">Added</span>}
            </label>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setDropSale(null)} className="btn btn-outline flex-1">Cancel</button>
              <button onClick={submitDrop} disabled={!dropPhoto || drop.isPending} className="btn btn-primary flex-1">
                {drop.isPending ? 'Confirming…' : 'Confirm drop-off'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute response modal */}
      {respSale && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setRespSale(null)}>
          <div className="card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="stamp text-bad">Respond to dispute</div>
            <h2 className="display text-2xl mt-1">{respSale.productTitle}</h2>
            {respSale.dispute && (
              <p className="text-sm text-mute mt-2">
                Buyer reported: <span className="text-ink font-medium">{REASON_LABELS[respSale.dispute.reason] ?? respSale.dispute.reason}</span>. Explain your side and add proof (e.g. your dispatch photo, tracking, or serial match).
              </p>
            )}
            <textarea className="input min-h-[110px] mt-4" placeholder="What actually happened? Reference your proof-of-dispatch, the item's serial, tracking, etc." maxLength={2000} value={respText} onChange={(e) => setRespText(e.target.value)} />
            <label className="mt-3 flex items-center gap-3 p-3 border-2 border-dashed border-warmline rounded-luxe cursor-pointer hover:border-gold transition">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setRespPhoto(e.target.files?.[0] ?? null)} />
              <span className="text-xl">📷</span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">{respPhoto ? respPhoto.name : 'Add counter-evidence photo (optional)'}</div>
                <div className="text-[11px] text-mute">{respPhoto ? 'Tap to replace' : 'Strengthens your case'}</div>
              </div>
              {respPhoto && <span className="pill pill-ok text-[9px]">Added</span>}
            </label>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setRespSale(null)} className="btn btn-outline flex-1">Cancel</button>
              <button onClick={submitResponse} disabled={respText.trim().length < 2 || respond.isPending} className="btn btn-primary flex-1">
                {respond.isPending ? 'Sending…' : 'Submit response'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shipping label / locker QR - the centralised hand-off. Souqli booked
          the label on its own carrier account; the seller just prints or scans. */}
      {labelSale && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setLabelSale(null)}>
          <div className="card p-6 max-w-md w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="stamp text-gold-dark">{labelSale.method === 'locker' ? 'Locker drop-off' : 'Shipping label'}</div>
            <h2 className="display text-2xl mt-1">{labelSale.productTitle}</h2>
            <p className="text-sm text-mute mt-2">
              {labelSale.method === 'locker'
                ? 'Souqli generated this drop-off code. Take it to your chosen InPost locker and scan it to send the parcel - no printing needed.'
                : 'Souqli generated this prepaid label on your behalf. Print it, attach it to your parcel, and drop it at any Post Office or pickup point.'}
            </p>

            {labelSale.method === 'locker' && labelSale.lockerDropQrUrl && (
              <img
                src={labelSale.lockerDropQrUrl}
                alt="Locker drop-off QR code"
                className="mx-auto mt-5 w-52 h-52 object-contain rounded-luxe border border-warmline bg-white p-2"
              />
            )}
            <button
              onClick={() => openLabelPdf(labelSale.id, labelSale.reference)}
              disabled={labelLoading}
              className="btn btn-gold !text-ink mt-5 inline-flex items-center gap-2"
            >
              🖨️ {labelLoading ? 'Preparing…' : 'Download label (PDF)'}
            </button>

            {labelSale.trackingCode && (
              <div className="stamp text-mute text-[10px] mt-4">TRACKING · <span className="mono text-ink">{labelSale.trackingCode}</span></div>
            )}
            <p className="stamp text-mute text-[9px] mt-4 leading-relaxed">
              🔒 Souqli is printed as the sender - your address stays private, and the buyer never sees it.
            </p>
            <button onClick={() => setLabelSale(null)} className="btn btn-outline w-full mt-5">Close</button>
          </div>
        </div>
      )}

      {trackSale && (
        <TrackingModal orderId={trackSale.id} title={trackSale.productTitle} onClose={() => setTrackSale(null)} />
      )}
      {complaintSale && (
        <ComplaintModal orderId={complaintSale.id} against="buyer" onClose={() => setComplaintSale(null)} />
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className={`notched-sm p-5 border border-warmline ${accent ?? 'bg-cream'}`}>
      <div className="stamp text-mute">{label}</div>
      <div className="display text-3xl mt-2">{value}</div>
    </div>
  );
}
